import {
  FLEET_CHARGE_CONNECTOR_LABELS,
  formatFleetKw,
  type FleetChargeConnector,
  type FleetChargeConnectorState,
  type FleetChargeVehicle,
} from '@gb-ticket/shared-types';

import { cn } from '@/lib/utils';

const TONE: Record<FleetChargeConnectorState, string> = {
  available:
    'border-green-200 bg-green-50 text-green-950 dark:border-green-900 dark:bg-green-950 dark:text-green-50',
  occupied:
    'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-50',
  charging:
    'animate-pulse border-sky-200 bg-sky-50 text-sky-950 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-50',
  faulted:
    'border-red-200 bg-red-50 text-red-950 dark:border-red-900 dark:bg-red-950 dark:text-red-50',
  unavailable: 'border-border bg-muted/40 text-foreground',
  offline: 'border-border bg-muted/40 text-foreground',
  unsupported: 'border-border bg-muted/40 text-foreground',
};

function metric(watts: number | null, unit: 'kW' | 'kWh'): string | null {
  if (watts == null) {
    return null;
  }
  return `${formatFleetKw(watts / 1000)} ${unit}`;
}

export function FleetChargeConnectorCard({ connector }: { connector: FleetChargeConnector }) {
  const power = metric(connector.powerW, 'kW');
  const energy = metric(connector.energyWh, 'kWh');
  return (
    <article className={cn('rounded-lg border px-3 py-3', TONE[connector.state])}>
      <p className="text-sm font-medium">{connector.stationName}</p>
      <p className="mt-1 text-lg font-semibold">Anschluss {connector.connectorNumber}</p>
      <p className="mt-2 text-sm font-semibold">{FLEET_CHARGE_CONNECTOR_LABELS[connector.state]}</p>
      {power || energy ? (
        <p className="mt-1 text-sm tabular-nums">{[power, energy].filter(Boolean).join(' · ')}</p>
      ) : null}
      {connector.licensePlate ? <p className="mt-2 text-sm">{connector.licensePlate}</p> : null}
      {connector.driverName ? <p className="text-sm">{connector.driverName}</p> : null}
      {connector.idTag ? <p className="text-sm">Chip {connector.idTag}</p> : null}
      {connector.lastError ? <p className="mt-2 text-sm">{connector.lastError}</p> : null}
    </article>
  );
}

function berlinTime(epochMs: number): string {
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
  }).format(epochMs);
}

export function FleetVehicleChargeLine({
  charge,
  failedAt,
}: {
  charge: FleetChargeVehicle | undefined;
  failedAt: number | null;
}) {
  if (!charge && failedAt != null) {
    return <p className="text-sm text-muted-foreground">Ladezustand nicht erreichbar</p>;
  }
  if (!charge) {
    return null;
  }
  return (
    <p className="text-sm text-muted-foreground">
      {charge.line}
      {failedAt != null ? ` Stand ${berlinTime(failedAt)}` : null}
    </p>
  );
}
